import omit from 'lodash/omit';
import type { StoreActionApi, GetState, SetState } from 'react-sweet-state';
import { ff } from '@atlassian/jira-feature-flagging';
import type {
	RegisteredFieldsMap,
	AppId,
	AppRegisteredFields,
	ChangeId,
	IssueAdjustmentsState,
	RegisteredFields,
	Buffer,
	BufferForChangeId,
} from '../../types';

import { findAppsToBufferForChangeId } from './find-apps-to-buffer';

/*
 * In this function we pre-fill buffer with null for the apps that have to be buffered.
 * Thanks to this in processIframeChanges action we know that the app should be buffered.
 */
export const computeBufferForChangeId = ({
	nextRegisteredFields,
	changeId,
	buffer,
}: {
	nextRegisteredFields: RegisteredFieldsMap;
	changeId: ChangeId;
	buffer: Buffer;
}) => {
	const currentBufferForChangeId = buffer[changeId] || {};

	const appsToBuffer: Set<AppId> = findAppsToBufferForChangeId({
		registeredFieldsMap: nextRegisteredFields,
		changeId,
	});

	const bufferForChangeId: BufferForChangeId = {};

	appsToBuffer.forEach((appIdToBuffer) => {
		bufferForChangeId[appIdToBuffer] = null;
	});

	return {
		...bufferForChangeId,
		...currentBufferForChangeId,
	};
};

const registerFieldsFFOn = ({
	getState,
	setState,
	changeId,
	appId,
	fieldsToBeRegistered,
}: {
	getState: GetState<IssueAdjustmentsState>;
	setState: SetState<IssueAdjustmentsState>;
	changeId: ChangeId;
	appId: AppId;
	fieldsToBeRegistered: RegisteredFields;
}) => {
	const { registeredFields, buffer } = getState();
	const appFields = registeredFields[appId] || {};
	const appChangeIdFields = appFields[changeId] || [];

	const nextRegisteredFields = {
		...registeredFields,
		[appId]: {
			...appFields,
			[changeId]: [...appChangeIdFields, ...fieldsToBeRegistered],
		},
	};

	const bufferForChangeId = computeBufferForChangeId({
		nextRegisteredFields,
		changeId,
		buffer,
	});

	setState({
		registeredFields: {
			...nextRegisteredFields,
		},
		buffer: {
			...buffer,
			...(Object.keys(bufferForChangeId).length && { [changeId]: bufferForChangeId }),
		},
	});
};

export const registerFields =
	({
		changeId,
		fieldsToBeRegistered,
		appId,
	}: {
		changeId: ChangeId;
		fieldsToBeRegistered: RegisteredFields;
		appId: AppId;
	}) =>
	({ setState, getState }: StoreActionApi<IssueAdjustmentsState>) => {
		if (!Array.isArray(fieldsToBeRegistered)) {
			return;
		}

		if (ff('ui-modifications-multi-app-experience_ud4yw')) {
			registerFieldsFFOn({
				getState,
				setState,
				changeId,
				appId,
				fieldsToBeRegistered,
			});
		} else {
			const alreadyRegisteredFields: AppRegisteredFields = getState().registeredFieldsDeprecated;

			setState({
				registeredFieldsDeprecated: {
					...alreadyRegisteredFields,
					[changeId]: fieldsToBeRegistered,
				},
			});
		}
	};

export const unregisterFields =
	({ changeId, appId }: { changeId: ChangeId; appId: AppId }) =>
	({ setState, getState }: StoreActionApi<IssueAdjustmentsState>) => {
		if (ff('ui-modifications-multi-app-experience_ud4yw')) {
			const { registeredFields } = getState();
			const registeredAppFields: AppRegisteredFields = registeredFields[appId] || {};
			const nextRegisteredAppFields = omit(registeredAppFields, changeId);

			setState({
				registeredFields: {
					...registeredFields,
					[appId]: nextRegisteredAppFields,
				},
			});
		} else {
			setState({
				registeredFieldsDeprecated: omit(getState().registeredFieldsDeprecated, changeId),
			});
		}
	};

export const unregisterAllFieldsOfChangeId =
	(changeId: ChangeId) =>
	({ setState, getState }: StoreActionApi<IssueAdjustmentsState>) => {
		const { registeredFields } = getState();
		const nextRegisteredFields: RegisteredFieldsMap = {};

		Object.entries(registeredFields).forEach(([appId, registeredAppFields]) => {
			if (registeredAppFields) {
				nextRegisteredFields[appId] = omit(registeredAppFields, changeId);
			}
		});

		setState({
			registeredFields: {
				...nextRegisteredFields,
			},
		});
	};
